<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useCallsStore } from 'dashboard/stores/calls';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const callsStore = useCallsStore();
const inboxes = ref([]);
const agentsByInbox = ref({});
const loading = ref(true);
const refreshing = ref(false);
let refreshTimer;

const voiceInboxes = computed(() =>
  inboxes.value.filter(
    inbox => inbox.channel_type === 'Channel::Whatsapp' && inbox.voice_enabled
  )
);

const activeWhatsappCalls = computed(() =>
  callsStore.calls.filter(call => call.provider === 'whatsapp')
);

const eligibleAgents = inbox => {
  const agents = agentsByInbox.value[inbox.id] || [];
  const selectedIds = (inbox.call_queue_config?.agent_ids || []).map(Number);
  return selectedIds.length
    ? agents.filter(agent => selectedIds.includes(Number(agent.id)))
    : agents;
};

const allAgents = computed(() => {
  const unique = new Map();
  voiceInboxes.value.forEach(inbox => {
    eligibleAgents(inbox).forEach(agent => unique.set(Number(agent.id), agent));
  });
  return [...unique.values()];
});

const onlineAgents = computed(
  () =>
    allAgents.value.filter(agent => agent.availability_status === 'online')
      .length
);
const busyAgents = computed(
  () =>
    allAgents.value.filter(agent => agent.availability_status === 'busy').length
);
const receivingInboxes = computed(
  () => voiceInboxes.value.filter(inbox => inbox.inbound_calls_enabled).length
);

const load = async (quiet = false) => {
  if (quiet) refreshing.value = true;
  else loading.value = true;

  try {
    const { data } = await InboxesAPI.get();
    const availableInboxes = (data.payload || []).filter(
      inbox => inbox.channel_type === 'Channel::Whatsapp' && inbox.voice_enabled
    );
    inboxes.value = availableInboxes;

    const agentResults = await Promise.all(
      availableInboxes.map(async inbox => {
        try {
          const response = await InboxesAPI.getAssignableAgents(inbox.id);
          return [inbox.id, response.data.payload || []];
        } catch {
          return [inbox.id, []];
        }
      })
    );
    agentsByInbox.value = Object.fromEntries(agentResults);
  } catch {
    if (!quiet) useAlert('Não foi possível carregar a central de ligações.');
  } finally {
    loading.value = false;
    refreshing.value = false;
  }
};

const agentStatusLabel = status => {
  if (status === 'online') return 'Disponível';
  if (status === 'busy') return 'Ocupado';
  return 'Offline';
};

const agentStatusClass = status => {
  if (status === 'online') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'busy') return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-alpha-2 text-n-slate-10';
};

const callStateLabel = call => (call.isActive ? 'Em atendimento' : 'Tocando');

onMounted(() => {
  load();
  refreshTimer = window.setInterval(() => load(true), 30_000);
});

onUnmounted(() => window.clearInterval(refreshTimer));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="flex-1 min-w-0 overflow-y-auto bg-n-background">
    <div class="w-full px-6 py-8 2xl:px-10">
      <header class="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div class="flex items-center gap-4">
          <div
            class="grid place-items-center size-12 rounded-xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-headset text-2xl" />
          </div>
          <div>
            <h1 class="text-2xl font-semibold text-n-slate-12">
              Central de ligações
            </h1>
            <p class="mt-1 text-sm text-n-slate-11">
              Operação de chamadas do WhatsApp e disponibilidade da equipe.
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            label="Atualizar"
            icon="i-lucide-refresh-cw"
            variant="outline"
            :disabled="refreshing || loading"
            @click="load()"
          />
          <RouterLink
            :to="{
              name: 'voice_queues_settings',
              params: { accountId: route.params.accountId },
            }"
            class="inline-flex"
          >
            <Button label="Configurações" icon="i-lucide-settings-2" />
          </RouterLink>
        </div>
      </header>

      <div v-if="loading" class="grid place-items-center min-h-64">
        <Spinner />
      </div>

      <template v-else>
        <section
          class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-7"
        >
          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">Linhas de voz</span>
              <i class="i-lucide-phone text-n-brand" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ voiceInboxes.length }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              WhatsApp Cloud configurado
            </p>
          </article>

          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">Recebimento ativo</span>
              <i class="i-lucide-phone-incoming text-n-teal-10" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ receivingInboxes }}
              <span class="text-base font-normal text-n-slate-10"
                >/ {{ voiceInboxes.length }}</span
              >
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              Linhas habilitadas para receber
            </p>
          </article>

          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">Agentes disponíveis</span>
              <i class="i-lucide-users-round text-n-brand" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ onlineAgents }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ busyAgents }} ocupados · {{ allAgents.length }} elegíveis nas
              filas
            </p>
          </article>

          <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">Chamadas para você</span>
              <i class="i-lucide-phone-call text-n-brand" />
            </div>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ activeWhatsappCalls.length }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              Alertas neste navegador, não total da empresa
            </p>
          </article>
        </section>

        <section
          v-if="activeWhatsappCalls.length"
          class="mb-7 rounded-xl border border-n-brand/30 bg-n-brand/5 p-5"
        >
          <div class="flex items-center justify-between gap-4 mb-4">
            <div>
              <h2 class="font-semibold text-n-slate-12">
                Sua atividade de chamadas
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                Chamadas oferecidas à sua sessão neste navegador.
              </p>
            </div>
            <span
              class="rounded-full px-2.5 py-1 text-xs bg-n-brand/10 text-n-brand"
              >{{ activeWhatsappCalls.length }} ativa(s)</span
            >
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <article
              v-for="call in activeWhatsappCalls"
              :key="call.callSid"
              class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-surface-1 p-3"
            >
              <div
                class="grid place-items-center size-10 rounded-full bg-n-brand/10 text-n-brand"
              >
                <i class="i-lucide-phone-incoming" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="font-medium text-n-slate-12 truncate">
                  {{ call.caller?.name || call.caller?.phone || 'Contato' }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ callStateLabel(call) }} · Caixa #{{ call.inboxId }}
                </p>
              </div>
            </article>
          </div>
        </section>

        <section class="mb-7">
          <div class="flex items-end justify-between gap-4 mb-4">
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                Linhas e filas
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                Estado da configuração e agentes elegíveis em cada caixa.
              </p>
            </div>
            <RouterLink
              :to="{
                name: 'voice_queues_settings',
                params: { accountId: route.params.accountId },
              }"
              class="text-sm text-n-brand hover:underline"
              >Gerenciar filas</RouterLink
            >
          </div>

          <div v-if="voiceInboxes.length" class="grid grid-cols-1 gap-4">
            <article
              v-for="inbox in voiceInboxes"
              :key="inbox.id"
              class="rounded-xl border border-n-weak bg-n-surface-1 overflow-hidden"
            >
              <div
                class="flex items-start justify-between gap-4 p-5 border-b border-n-weak"
              >
                <div class="flex items-center gap-3 min-w-0">
                  <img
                    v-if="inbox.avatar_url"
                    :src="inbox.avatar_url"
                    alt=""
                    class="size-10 rounded-full"
                  />
                  <div
                    v-else
                    class="grid place-items-center size-10 rounded-full bg-n-brand/10 text-n-brand font-semibold"
                  >
                    {{ inbox.name?.charAt(0) || 'W' }}
                  </div>
                  <div class="min-w-0">
                    <h3 class="font-semibold text-n-slate-12 truncate">
                      {{ inbox.name }}
                    </h3>
                    <p class="mt-1 text-xs text-n-slate-10">
                      WhatsApp Cloud · Caixa #{{ inbox.id }}
                    </p>
                  </div>
                </div>
                <span
                  class="shrink-0 rounded-full px-2.5 py-1 text-xs"
                  :class="
                    inbox.inbound_calls_enabled
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  {{ inbox.inbound_calls_enabled ? 'Recebendo' : 'Pausada' }}
                </span>
              </div>

              <div class="p-5">
                <div class="flex items-center justify-between gap-4 mb-3">
                  <div>
                    <p class="text-sm font-medium text-n-slate-12">
                      {{
                        inbox.call_queue_config?.strategy === 'ring_all'
                          ? 'Tocar para todos'
                          : 'Responsável primeiro'
                      }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ eligibleAgents(inbox).length }} agente(s) elegível(is)
                      ·
                      {{
                        eligibleAgents(inbox).filter(
                          agent => agent.availability_status === 'online'
                        ).length
                      }}
                      disponível(is) agora
                    </p>
                  </div>
                  <RouterLink
                    :to="{
                      name: 'voice_queues_settings',
                      params: { accountId: route.params.accountId },
                    }"
                    class="grid place-items-center size-9 rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
                    :aria-label="`Configurar fila ${inbox.name}`"
                  >
                    <i class="i-lucide-settings-2" />
                  </RouterLink>
                </div>

                <div
                  v-if="eligibleAgents(inbox).length"
                  class="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2"
                >
                  <span
                    v-for="agent in eligibleAgents(inbox)"
                    :key="agent.id"
                    class="inline-flex min-w-0 items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
                  >
                    <span
                      class="size-2 rounded-full"
                      :class="
                        agent.availability_status === 'online'
                          ? 'bg-n-teal-9'
                          : agent.availability_status === 'busy'
                            ? 'bg-n-amber-9'
                            : 'bg-n-slate-8'
                      "
                    />
                    <span
                      class="min-w-0 flex-1 truncate text-sm text-n-slate-12"
                      >{{ agent.name }}</span
                    >
                    <span
                      class="text-[11px] rounded-full px-2 py-0.5"
                      :class="agentStatusClass(agent.availability_status)"
                      >{{ agentStatusLabel(agent.availability_status) }}</span
                    >
                  </span>
                </div>
                <p v-else class="text-sm text-n-amber-11">
                  Nenhum agente elegível retornado para esta fila.
                </p>
              </div>
            </article>
          </div>

          <div
            v-else
            class="rounded-xl border border-dashed border-n-weak bg-n-surface-1 p-10 text-center"
          >
            <i class="i-lucide-phone-off text-3xl text-n-slate-9" />
            <h3 class="mt-3 font-medium text-n-slate-12">
              Nenhuma linha de chamadas ativa
            </h3>
            <p class="mt-1 text-sm text-n-slate-11 max-w-xl mx-auto">
              Ative chamadas do WhatsApp Cloud e o recebimento nas configurações
              da caixa para ela aparecer nesta central.
            </p>
            <RouterLink
              :to="{
                name: 'settings_inbox_list',
                params: { accountId: route.params.accountId },
              }"
              class="inline-flex mt-4 text-sm text-n-brand hover:underline"
              >Abrir configurações das caixas</RouterLink
            >
          </div>
        </section>

        <p class="text-xs text-n-slate-10 border-t border-n-weak pt-4">
          Os status de agente refletem a disponibilidade no atendimento da
          Lumeratec; não indicam registro de ramal ou presença telefônica. A
          Cloud API controla a chamada e os alertas, mas não fornece recursos de
          PABX como espera com música, URA ou transbordo temporizado.
        </p>
      </template>
    </div>
  </main>
</template>
